'use client';

import { useMemo } from 'react';
import { useTargetId, useTargetType } from '../../../store/projectStore';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { useSignals } from '../../../hooks/useSignals';
import { useSignalCounts } from '../../../hooks/useSignalCounts';
import { useSelectableSignalTotal } from '../../../hooks/useSelectableSignalTotal';
import {
  boothVaultAttackSignalIndex,
  orderSignalsForBoothVault,
} from '../../../utils/booth-signal-order';
import { resolveSignalHeaderTotal } from '../../../utils/resolve-signal-header-total';
import type { SignalsListFilters } from './useSignalsListFilters';
import type { SignalsScopeModel } from './useSignalsScope';
import { buildSignalStageChipCountsFilters } from './signals-stage-chips.model';

export type SignalsListSource = {
  endpointId?: string;
  surfaceAssetEntityId?: string;
  surfaceAssetKind?: string;
};

export function useSignalsListData({
  source,
  listFilters,
  scopeModel,
}: {
  source: SignalsListSource;
  listFilters: SignalsListFilters;
  scopeModel: SignalsScopeModel;
}) {
  const targetId = useTargetId();
  const targetType = useTargetType();
  const { scope, isScopeFilterPending, domainTreeScopedTotalCount, aiKindNav } =
    scopeModel;
  const { isRejectedSignalsView } = listFilters;
  const isCountsEnabled = !isRejectedSignalsView && !isScopeFilterPending;
  const baseQuery = {
    targetId,
    targetType,
    endpointId: source.endpointId,
    surfaceAssetEntityId: source.surfaceAssetEntityId,
    surfaceAssetKind: source.surfaceAssetKind,
    includeApplicationLevelGroup: scope.includeApplicationLevelGroup,
  };
  const { data: countsData } = useSignalCounts({
    ...baseQuery,
    filters: listFilters.countsFilters,
    enabled: isCountsEnabled,
  });
  const stageChipCountsFilters = useMemo(
    () => buildSignalStageChipCountsFilters(listFilters.countsFilters),
    [listFilters.countsFilters],
  );
  const { data: stageCounts } = useSignalCounts({
    ...baseQuery,
    filters: stageChipCountsFilters,
    enabled: isCountsEnabled,
  });
  const { data: totalCountsData } = useSignalCounts({
    ...baseQuery,
    filters: listFilters.totalCountsFilters,
    enabled: isCountsEnabled && listFilters.fetchUnfilteredSignalTotal,
  });
  const signalsQuery = useSignals({
    ...baseQuery,
    filters: listFilters.filters,
    endpointIds: scope.endpointIds,
    componentTypes: scope.componentTypes,
    urlPatterns: scope.urlPatterns,
    rejectedOnly: isRejectedSignalsView,
    enabled: !isScopeFilterPending,
  });
  const { isFeatureFlagEnabled: isBoothTourEnabled } =
    useFeatureFlags('BOOTH_TOUR_ENABLED');
  const signals = useMemo(
    () =>
      isBoothTourEnabled
        ? orderSignalsForBoothVault(signalsQuery.signals)
        : signalsQuery.signals,
    [isBoothTourEnabled, signalsQuery.signals],
  );
  const boothAttackSignalId = useMemo(
    () =>
      isBoothTourEnabled
        ? signals[boothVaultAttackSignalIndex(signals)]?.id
        : undefined,
    [isBoothTourEnabled, signals],
  );
  const selectableTotalCount = useSelectableSignalTotal({
    ...baseQuery,
    displayedTotalCount: signalsQuery.totalCount,
    lifecycleQueryParam: listFilters.lifecycle.lifecycleQueryParam,
    countsFilters: listFilters.countsFilters,
    enabled: isCountsEnabled,
  });
  const headerTotalCount = resolveSignalHeaderTotal({
    isRejectedSignalsView,
    isAiApplication: aiKindNav.isAiApplication,
    listTotalCount: signalsQuery.totalCount,
    unfilteredAllCount: totalCountsData?.counts.ALL,
    domainTreeScopedTotalCount,
  });
  const hasActiveFilters =
    listFilters.hasActiveFilters ||
    scopeModel.hasScopedDomainTreeFilter ||
    (aiKindNav.isAiApplication && aiKindNav.selectedKind !== 'all');
  const filteredCount =
    isRejectedSignalsView || hasActiveFilters
      ? signalsQuery.totalCount
      : countsData?.counts.ALL;

  return {
    ...signalsQuery,
    signals,
    boothAttackSignalId,
    selectableTotalCount,
    headerTotalCount,
    filteredCount,
    stageCounts,
    hasActiveFilters,
  };
}

export type SignalsListData = ReturnType<typeof useSignalsListData>;
